/**
 * DEV-2050 - Helpers for telling a seller that a bundled order ships as one parcel.
 *
 * The API reports how many live orders share the bundle. It reports null for orders created
 * before bundle groups existed, and those keep whatever bundled-or-not signal the screen used
 * before. A group whose siblings were all cancelled counts 1 and reads as a single item.
 */
import { OrderType } from 'types/order.types';

export const getBundleItemsCount = (order?: Partial<OrderType> | null): number | null =>
  order?.bundleItemsCount ?? order?.bundle_items_count ?? null;

/**
 * The statuses the API leaves out of `bundleItemsCount`, kept in step with NON_LIVE_BUNDLE_STATUSES
 * on the server. An order in one of these is no longer part of a shipment, so the count says
 * nothing useful about it: it excludes the order from its own count, which is 0 for a group
 * that was cancelled outright and 1 when a sibling survived.
 *
 * These orders get no bundle mark at all. The mark sits on the delivery line and reads as an
 * instruction to pack more than one item, and a cancelled order has nothing left to pack.
 * That also settles a pre-existing inconsistency: the old flag showed the mark on a cancelled
 * order only when it happened to be the one holding the shipping fee.
 */
const NON_LIVE_BUNDLE_STATUSES: string[] = ['cancelled', 'payment-cancelled', 'submitted'];

const isLiveOrder = (status: string | undefined | null): boolean => !!status && !NON_LIVE_BUNDLE_STATUSES.includes(status);

export const isBundledOrder = ({
  status,
  bundleItemsCount,
  legacyIsBundled,
}: {
  status: string | undefined | null;
  bundleItemsCount: number | null | undefined;
  legacyIsBundled: boolean;
}): boolean => {
  if (!isLiveOrder(status)) {
    return false;
  }

  return bundleItemsCount == null ? legacyIsBundled : bundleItemsCount > 1;
};

/** Narrows to the case where the copy can name the number of items rather than just say "Bundle". */
export const hasBundleItemsCount = (bundleItemsCount: number | null | undefined): bundleItemsCount is number =>
  bundleItemsCount != null && bundleItemsCount > 1;

/** The i18next context that swaps a single-item string for its bundle wording. */
export const bundleContext = (bundleItemsCount: number | null | undefined): 'bundle' | undefined =>
  hasBundleItemsCount(bundleItemsCount) ? 'bundle' : undefined;
